/**
 * @fileoverview Utilitário para extração e tratamento de mensagens de erro
 * Padroniza a forma como extraímos e apresentamos mensagens de erro ao usuário
 */

import { isString } from "lodash";
import { returnBackendError } from "../translate/messages";

/**
 * Extrai a mensagem de erro de um objeto de erro
 * 
 * @param {Error|Object} oErro - Objeto de erro que pode vir da API ou ser um erro JavaScript
 * @param {string} cMensagemPadrao - Mensagem padrão caso não seja possível extrair uma mensagem do erro
 * @returns {string} A mensagem de erro formatada
 */
export const erroMensagemExtrair = (oErro, cMensagemPadrao = "Ocorreu um erro inesperado") => {
  // Se não houver erro, retorna a mensagem padrão
  if (!oErro) {
    return cMensagemPadrao;
  }

  // Verifica se é uma resposta da API com estrutura de erro
  if (oErro.response && oErro.response.data) {
    // Tenta encontrar a mensagem de erro nos formatos conhecidos da API
    const cErroTraduzido = oErro.response.data.error 
      ? returnBackendError(oErro.response.data.error) 
      : null;
    
    if (cErroTraduzido) {
      return cErroTraduzido;
    }
    
    if (oErro.response.data.error) {
      return oErro.response.data.error;
    }
    
    if (oErro.response.data.message) {
      return oErro.response.data.message;
    }
    
    // Para APIs que retornam mensagens em outros formatos
    if (oErro.response.data.mensagem) {
      return oErro.response.data.mensagem;
    }
  }

  // Verifica se o erro contém o código de status HTTP
  if (oErro.response && oErro.response.status) {
    const nStatus = oErro.response.status;
    
    // Tratamento especial para códigos HTTP comuns
    if (nStatus === 401) {
      return "Não autorizado. Por favor, faça login novamente.";
    }
    
    if (nStatus === 403) {
      return "Você não tem permissão para realizar esta operação.";
    }
    
    if (nStatus === 404) {
      return "O recurso solicitado não foi encontrado.";
    }
    
    if (nStatus === 500) {
      return "Erro interno do servidor. Por favor, tente novamente mais tarde.";
    }
  }

  // Verifica se é um erro com a propriedade message (erro padrão do JavaScript)
  if (oErro.message) {
    return oErro.message;
  }

  // Se o erro for apenas uma string
  if (isString(oErro)) {
    return oErro;
  }

  // Caso nenhum formato conhecido seja encontrado
  return cMensagemPadrao;
};

/**
 * Registra o erro no console e retorna a mensagem formatada
 * 
 * @param {Error|Object} oErro - Objeto de erro
 * @param {string} cMensagemPadrao - Mensagem padrão
 * @returns {string} Mensagem de erro formatada
 */
export const erroTratar = (oErro, cMensagemPadrao = "Ocorreu um erro inesperado") => {
  // Registra o erro completo no console para depuração
  console.error("Erro:", oErro);
  
  // Retorna a mensagem formatada para exibição
  return erroMensagemExtrair(oErro, cMensagemPadrao);
};

/**
 * Cria um objeto de erro padronizado
 * 
 * @param {string} cMensagem - Mensagem de erro
 * @param {number} nCodigo - Código do erro
 * @param {Object} oDetalhes - Detalhes adicionais do erro
 * @returns {Object} Objeto de erro padronizado
 */
export const erroObjetoCriar = (cMensagem, nCodigo = 500, oDetalhes = {}) => {
  const oErro = new Error(cMensagem);
  oErro.codigo = nCodigo;
  oErro.detalhes = oDetalhes;
  return oErro;
};